<section
  class="relative flex h-full w-full flex-col overflow-hidden bg-white text-slate-900"
  role="dialog"
  [attr.aria-label]="'stock.product_form.title_new' | translate"
>
  <header class="flex shrink-0 items-center gap-3 border-b !border-slate-100 px-5 py-3">
    <div class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-orange-50 text-orange-600">
      <app-icon>inventory_2</app-icon>
    </div>
    <div class="min-w-0 flex-1">
      <h2 class="m-0 truncate text-base font-bold leading-6 text-slate-900">
        {{ isNew ? ('stock.product_form.title_new' | translate) : product?.name }}
      </h2>
      @if (product?.is_archived) {
        <span class="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
          {{ 'stock.archived' | translate }}
        </span>
      }
    </div>
    @if (product; as saved) {
      <button
        type="button"
        class="inline-flex h-9 items-center gap-1.5 rounded-lg border !border-slate-200 px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
        [disabled]="busy || saving"
        (click)="toggleArchive(saved)"
      >
        <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">{{
          saved.is_archived ? 'unarchive' : 'archive'
        }}</app-icon>
        {{ (saved.is_archived ? 'restore' : 'archive') | translate }}
      </button>
      <button
        type="button"
        class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
        [matTooltip]="'delete' | translate"
        [disabled]="busy || saving"
        (click)="deleteProduct(saved)"
      >
        <app-icon class="!h-5 !w-5 !text-[20px] !leading-none">delete</app-icon>
      </button>
    }
    <button
      type="button"
      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
      [attr.aria-label]="'close' | translate"
      (click)="requestClose()"
    >
      <app-icon>close</app-icon>
    </button>
  </header>

  @if (loading) {
    <div class="flex flex-1 flex-col gap-4 bg-slate-50 p-5">
      <div class="h-48 animate-pulse rounded-lg bg-white"></div>
      <div class="h-28 animate-pulse rounded-lg bg-white"></div>
      <div class="h-64 animate-pulse rounded-lg bg-white"></div>
    </div>
  } @else if (loadFailed) {
    <div class="flex flex-1 flex-col items-center justify-center gap-3 bg-slate-50 p-5 text-center">
      <app-icon class="!text-[32px] text-slate-300">error_outline</app-icon>
      <p class="m-0 text-sm text-slate-600">{{ 'stock.load_failed' | translate }}</p>
      <app-button btnType="stroked" (onChangeClick)="load()">{{ 'stock.retry' | translate }}</app-button>
    </div>
  } @else {
    <div class="crm-scroll min-h-0 flex-1 overflow-y-auto bg-slate-50 px-5 py-4">
      <div class="flex flex-col gap-4">
        <section class="rounded-lg border !border-slate-200 bg-white p-4">
          <h3 class="m-0 mb-3 text-sm font-semibold text-slate-900">{{ 'stock.product_form.main' | translate }}</h3>
          <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
            <app-input
              class="md:col-span-3"
              [formControl]="form.controls.name"
              [label]="'stock.product_form.name' | translate"
              [required]="true"
              [maxlength]="255"
            />
            <app-select
              [formControl]="form.controls.category_id"
              [options]="categories"
              optionLabel="path"
              optionValue="id"
              [filter]="true"
              [showClear]="true"
              [label]="'category' | translate"
              [placeholder]="'stock.no_category' | translate"
            />
            <app-select
              [formControl]="form.controls.unit_id"
              [options]="units"
              optionLabel="name"
              optionValue="id"
              [filter]="true"
              [required]="true"
              [label]="'stock.unit' | translate"
            />
            <app-select
              [formControl]="form.controls.currency"
              [options]="currencyOptions"
              optionLabel="label"
              optionValue="value"
              [label]="'stock.currency' | translate"
            />
            <div class="md:col-span-3">
              <app-select
                [formControl]="form.controls.tax_group"
                [options]="taxOptions"
                optionLabel="name"
                optionValue="id"
                [isTranslate]="true"
                [showClear]="true"
                [label]="'stock.tax_group' | translate"
                [placeholder]="'stock.tax_group_default' | translate"
              />
              <p class="!mb-0 mt-1 text-xs text-slate-500">{{ 'stock.tax_group_hint' | translate }}</p>
            </div>
            <app-textarea
              class="md:col-span-3"
              [formControl]="form.controls.description"
              [label]="'description' | translate"
              [maxLength]="10000"
            />
          </div>
          @if (!isNew) {
            <p class="m-0 mt-2 text-xs text-slate-400">{{ 'stock.product_form.currency_hint' | translate }}</p>
          }
        </section>

        <section class="rounded-lg border !border-slate-200 bg-white p-4">
          <div class="mb-3 flex items-center gap-2">
            <h3 class="m-0 text-sm font-semibold text-slate-900">{{ 'stock.product_form.photos' | translate }}</h3>
            <span class="text-xs text-slate-400">{{ 'stock.product_form.photos_hint' | translate }}</span>
          </div>
          <div
            cdkDropList
            cdkDropListOrientation="mixed"
            class="flex flex-wrap gap-2"
            (cdkDropListDropped)="dropProductPhoto($event)"
          >
            @for (photoId of photoIds; track photoId; let first = $first) {
              <div
                cdkDrag
                class="group relative h-24 w-24 cursor-grab overflow-hidden rounded-lg bg-slate-100 ring-1 ring-slate-200"
              >
                <img class="h-full w-full object-cover" [src]="getFileUrl(photoId)" alt="" />
                @if (first) {
                  <span
                    class="absolute bottom-1 left-1 rounded bg-white/90 px-1.5 text-[10px] font-semibold text-slate-700"
                  >
                    {{ 'stock.product_form.main_photo' | translate }}
                  </span>
                }
                <button
                  type="button"
                  class="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-white/90 text-slate-500 shadow transition hover:text-rose-600"
                  [attr.aria-label]="'delete' | translate"
                  (click)="photoIds.splice($index, 1)"
                >
                  <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">close</app-icon>
                </button>
              </div>
            }
            <label
              class="!flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border !border-dashed !border-slate-300 text-center text-xs font-medium text-slate-400 transition hover:!border-orange-300 hover:bg-orange-50 hover:text-orange-600"
            >
              <input
                #photosInput
                type="file"
                accept="image/*"
                multiple
                class="hidden"
                (change)="onPhotosPicked(photosInput)"
              />
              @if (uploading) {
                <span class="h-5 w-5 animate-spin rounded-full border-2 !border-slate-200 border-t-orange-500"></span>
              } @else {
                <app-icon class="!h-6 !w-6 !text-[24px] !leading-none">add_photo_alternate</app-icon>
              }
              {{ 'add_photo' | translate }}
            </label>
          </div>
        </section>

        <section class="rounded-lg border !border-slate-200 bg-white p-4">
          <div class="mb-3 flex flex-wrap items-center gap-2">
            <h3 class="m-0 text-sm font-semibold text-slate-900">{{ 'stock.product_form.variants' | translate }}</h3>
            <span class="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{{
              rows.length
            }}</span>
            <div class="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="inline-flex h-8 items-center gap-1 rounded-lg border !border-slate-200 px-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                [cdkMenuTriggerFor]="propertyMenu"
              >
                <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
                {{ 'stock.product_form.add_property' | translate }}
              </button>
              @if (propertyIds.length) {
                <button
                  type="button"
                  class="inline-flex h-8 items-center gap-1 rounded-lg border px-2.5 text-xs font-semibold transition"
                  [ngClass]="
                    generatorOpen
                      ? '!border-orange-300 bg-orange-50 text-orange-700'
                      : '!border-slate-200 text-slate-600 hover:bg-slate-50'
                  "
                  (click)="generatorOpen = !generatorOpen"
                >
                  <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">auto_awesome</app-icon>
                  {{ 'stock.product_form.generate' | translate }}
                </button>
              }
            </div>
          </div>

          <ng-template #propertyMenu>
            <div
              cdkMenu
              class="crm-scroll flex max-h-72 min-w-56 flex-col overflow-y-auto rounded-lg border !border-slate-200 bg-white py-1 shadow-lg"
            >
              @for (property of otherProperties; track property.id) {
                <button
                  type="button"
                  cdkMenuItem
                  class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                  (cdkMenuItemTriggered)="addPropertyColumn(property)"
                >
                  <span class="truncate">{{ property.name }}</span>
                  <span class="text-xs text-slate-400">{{ property.values.length }}</span>
                </button>
              } @empty {
                <div class="px-3 py-2 text-sm text-slate-400">
                  {{ 'stock.product_form.no_more_properties' | translate }}
                </div>
              }
            </div>
          </ng-template>

          <div class="mb-3 flex flex-wrap items-stretch gap-2">
            @for (property of chosenProperties; track property.id) {
              <div class="flex w-56 flex-col gap-2 rounded-lg border !border-slate-200 bg-slate-50 p-2">
                <div class="flex items-center gap-1">
                  <span class="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{{ property.name }}</span>
                  <span class="text-xs text-slate-400">{{ property.values.length }}</span>
                  <button
                    type="button"
                    class="grid h-6 w-6 place-items-center rounded text-slate-400 hover:bg-white hover:text-rose-600"
                    [matTooltip]="'stock.product_form.remove_property' | translate"
                    (click)="removePropertyColumn(property)"
                  >
                    <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">close</app-icon>
                  </button>
                </div>
                <ng-container
                  [ngTemplateOutlet]="addInput"
                  [ngTemplateOutletContext]="{ property, placeholder: 'stock.product_form.new_value' }"
                />
              </div>
            }
            <div class="flex w-56 flex-col justify-center gap-2 rounded-lg border !border-dashed !border-slate-200 p-2">
              <span class="text-xs font-medium text-slate-500">{{
                'stock.product_form.new_property' | translate
              }}</span>
              <ng-container
                [ngTemplateOutlet]="addInput"
                [ngTemplateOutletContext]="{ placeholder: 'stock.product_form.new_property_placeholder' }"
              />
            </div>
          </div>

          <!-- A new value of `property`, a new property without it -->
          <ng-template #addInput let-property="property" let-placeholder="placeholder">
            <div class="flex items-center gap-1">
              <input
                type="text"
                maxlength="255"
                class="h-8 min-w-0 flex-1 rounded-md border !border-slate-200 bg-white px-2 text-xs text-slate-800 outline-none focus:!border-orange-400"
                [placeholder]="placeholder | translate"
                [ngModel]="property ? (newValueTexts[property.id] ?? '') : newPropertyName"
                (ngModelChange)="property ? (newValueTexts[property.id] = $event) : (newPropertyName = $event)"
                (keydown.enter)="$event.preventDefault(); property ? addPropertyValue(property) : createProperty()"
              />
              <button
                type="button"
                class="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-white text-slate-500 ring-1 ring-slate-200 hover:text-orange-600 disabled:opacity-50"
                [disabled]="busy"
                [attr.aria-label]="'add' | translate"
                (click)="property ? addPropertyValue(property) : createProperty()"
              >
                <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
              </button>
            </div>
          </ng-template>

          @if (generatorOpen && propertyIds.length) {
            <div class="mb-3 flex flex-col gap-3 rounded-lg border !border-orange-200 bg-orange-50/50 p-3">
              <p class="m-0 text-xs text-slate-600">{{ 'stock.product_form.generate_hint' | translate }}</p>
              <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                @for (property of chosenProperties; track property.id) {
                  <app-select
                    [type]="SelectType.Multiple"
                    [label]="property.name"
                    [options]="property.values"
                    optionLabel="value"
                    optionValue="id"
                    [filter]="true"
                    display="chip"
                    [maxSelectedLabels]="10"
                    [(ngModel)]="generatorValues[property.id]"
                  />
                }
              </div>
              <div class="flex justify-end gap-2">
                <app-button btnType="cancel" (onChangeClick)="generatorOpen = false">{{
                  'cancel' | translate
                }}</app-button>
                <app-button [disabled]="!generatedCount" (onChangeClick)="generateVariants()">
                  {{ 'stock.product_form.generate_count' | translate: { count: generatedCount } }}
                </app-button>
              </div>
            </div>
          }

          <app-table-v2
            layout="embedded"
            size="sm"
            minWidth="min-w-[860px]"
            [columns]="variantColumns"
            [rows]="rows"
            [actions]="variantActions"
            actionsClass="w-36"
            [rowClass]="archivedRowClass"
          >
            <ng-template appTableV2Header="purchase">
              @if (isNew) {
                {{ 'stock.document.purchase_price' | translate }}, {{ currencySymbol }}
              } @else {
                {{ 'stock.cost_price' | translate }}
              }
            </ng-template>
            <ng-template appTableV2Header="price">
              {{ 'stock.sale_price' | translate }}, {{ currencySymbol }}
            </ng-template>
            <ng-template appTableV2Header="stock">
              @if (isNew) {
                {{ 'stock.quantity' | translate }}{{ unitShortName ? ', ' + unitShortName : '' }}
              } @else {
                {{ 'stock.available' | translate }}
              }
            </ng-template>

            <ng-template appTableV2Cell="photo" [appTableV2CellOf]="rows" let-row>
              <input
                #rowPhotoInput
                type="file"
                accept="image/*"
                class="hidden"
                (change)="onPhotosPicked(rowPhotoInput, row)"
              />
              <button
                type="button"
                class="block rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300"
                [matTooltip]="'stock.product_form.variant_photo' | translate"
                [cdkMenuTriggerFor]="photoMenu"
              >
                <app-warehouse-thumb [fileId]="row.photo_id" size="sm" />
              </button>
              <ng-template #photoMenu>
                <div
                  cdkMenu
                  class="flex w-64 flex-col gap-2 rounded-lg border !border-slate-200 bg-white p-2 shadow-lg"
                >
                  <div class="grid grid-cols-4 gap-1.5">
                    @for (photoId of photoIds; track photoId) {
                      <button
                        type="button"
                        cdkMenuItem
                        class="h-12 w-12 overflow-hidden rounded-md ring-2 transition"
                        [ngClass]="
                          row.photo_id === photoId ? 'ring-orange-500' : 'ring-transparent hover:ring-slate-300'
                        "
                        (cdkMenuItemTriggered)="row.photo_id = photoId"
                      >
                        <img class="h-full w-full object-cover" [src]="getFileUrl(photoId)" alt="" />
                      </button>
                    } @empty {
                      <p class="col-span-4 m-0 px-1 text-xs text-slate-400">
                        {{ 'stock.product_form.no_product_photos' | translate }}
                      </p>
                    }
                  </div>
                  <button
                    type="button"
                    cdkMenuItem
                    class="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                    (cdkMenuItemTriggered)="rowPhotoInput.click()"
                  >
                    <app-icon class="!h-4 !w-4 !text-[16px] !leading-none text-slate-400">upload</app-icon>
                    {{ 'stock.product_form.upload_photo' | translate }}
                  </button>
                  @if (row.photo_id) {
                    <button
                      type="button"
                      cdkMenuItem
                      class="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-rose-600 hover:bg-rose-50"
                      (cdkMenuItemTriggered)="row.photo_id = null"
                    >
                      <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">hide_image</app-icon>
                      {{ 'stock.product_form.remove_photo' | translate }}
                    </button>
                  }
                </div>
              </ng-template>
            </ng-template>
            @for (property of chosenProperties; track property.id) {
              <ng-template [appTableV2Cell]="'property:' + property.id" [appTableV2CellOf]="rows" let-row>
                <app-select
                  class="block"
                  [ngClass]="row.invalid.has('values') ? '[&_.p-select]:!border-red-400' : ''"
                  size="small"
                  [options]="property.values"
                  optionLabel="value"
                  optionValue="id"
                  [filter]="true"
                  [showClear]="true"
                  placeholder="—"
                  [ngModel]="row.values[property.id] ?? null"
                  (ngModelChange)="setRowValue(row, property.id, $event)"
                />
              </ng-template>
            }
            <ng-template appTableV2Cell="sku" [appTableV2CellOf]="rows" let-row>
              <ng-container [ngTemplateOutlet]="rowInput" [ngTemplateOutletContext]="{ row, field: 'sku' }" />
            </ng-template>
            <ng-template appTableV2Cell="barcode" [appTableV2CellOf]="rows" let-row>
              <ng-container [ngTemplateOutlet]="rowInput" [ngTemplateOutletContext]="{ row, field: 'barcode' }" />
            </ng-template>
            <ng-template appTableV2Cell="purchase" [appTableV2CellOf]="rows" let-row>
              @if (row.variant) {
                {{ formatMoneyAmount(row.variant.cost_price, form.controls.currency.value) }}
              } @else {
                <ng-container
                  [ngTemplateOutlet]="rowInput"
                  [ngTemplateOutletContext]="{ row, field: 'purchase_price', placeholder: '0' }"
                />
              }
            </ng-template>
            <ng-template appTableV2Cell="price" [appTableV2CellOf]="rows" let-row>
              <ng-container
                [ngTemplateOutlet]="rowInput"
                [ngTemplateOutletContext]="{ row, field: 'price', placeholder: '0' }"
              />
            </ng-template>
            <ng-template appTableV2Cell="stock" [appTableV2CellOf]="rows" let-row>
              @if (row.variant) {
                <span class="font-medium text-slate-800">{{ formatQuantity(row.variant.stock.available) }}</span>
                <span class="text-xs text-slate-400">
                  / {{ formatQuantity(row.variant.stock.quantity) }} {{ unitShortName }}</span
                >
              } @else {
                <ng-container
                  [ngTemplateOutlet]="rowInput"
                  [ngTemplateOutletContext]="{ row, field: 'quantity', placeholder: '0' }"
                />
              }
            </ng-template>

            <ng-template appTableV2RowAfter [appTableV2RowAfterOf]="rows" let-row let-colspan="colspan">
              @if (quickStock?.row === row && quickStock; as action) {
                <tr class="!border-t !border-slate-100">
                  <td class="px-2 py-2" [attr.colspan]="colspan">
                    <ng-container [ngTemplateOutlet]="quickStockPanel" [ngTemplateOutletContext]="{ action }" />
                  </td>
                </tr>
              }
            </ng-template>
          </app-table-v2>

          <button
            type="button"
            class="mt-2 flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700"
            (click)="addRow()"
          >
            <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
            {{ 'stock.product_form.add_variant' | translate }}
          </button>

          @if (hasNewRows) {
            <div
              class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2.5"
              [ngClass]="stockWarehouseError ? '!border-red-200 bg-red-50' : '!border-slate-200 bg-slate-50'"
            >
              <span class="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-700">
                <app-icon class="!h-5 !w-5 !text-[20px] !leading-none">move_to_inbox</app-icon>
              </span>
              @if (warehouses.length) {
                <span class="text-sm font-medium text-slate-700">{{
                  'stock.product_form.stock_warehouse' | translate
                }}</span>
                <app-select
                  class="w-60"
                  size="small"
                  [options]="warehouses"
                  optionLabel="name"
                  optionValue="id"
                  [filter]="warehouses.length > 7"
                  [(ngModel)]="stockWarehouseId"
                  (ngModelChange)="stockWarehouseError = false"
                />
                <span
                  class="min-w-0 flex-1 text-xs"
                  [ngClass]="stockWarehouseError ? 'text-red-600' : 'text-slate-500'"
                >
                  @if (stockWarehouseError) {
                    {{ 'stock.product_form.error_stock_warehouse' | translate }}
                  } @else if (openingTotal.quantity > 0) {
                    {{
                      'stock.product_form.stock_summary'
                        | translate
                          : {
                              quantity: formatQuantity(openingTotal.quantity),
                              unit: unitShortName,
                              amount: formatMoneyAmount(openingTotal.amount, form.controls.currency.value),
                            }
                    }}
                  } @else {
                    {{ 'stock.product_form.stock_hint' | translate }}
                  }
                </span>
              } @else {
                <span
                  class="min-w-0 flex-1 text-sm"
                  [ngClass]="stockWarehouseError ? 'text-red-600' : 'text-slate-600'"
                  >{{ 'stock.product_form.no_warehouses' | translate }}</span
                >
                <app-button btnType="stroked" (onChangeClick)="createWarehouse()">
                  {{ 'stock.product_form.create_warehouse' | translate }}
                </app-button>
              }
            </div>
          }

          @if (errorMessages.length) {
            <div
              class="mt-3 flex flex-col gap-1 rounded-lg border !border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600"
            >
              @for (message of errorMessages; track message) {
                <span>{{ message }}</span>
              }
            </div>
          }
        </section>
      </div>
    </div>

    <footer class="flex shrink-0 gap-2 border-t !border-slate-100 px-5 py-3">
      <app-button
        btnType="stroked"
        class="flex-1"
        [customClass]="'w-full !h-10 !rounded-lg'"
        (onChangeClick)="requestClose()"
      >
        {{ 'cancel' | translate }}
      </app-button>
      <app-button
        class="flex-1"
        [customClass]="'w-full !h-10 !rounded-lg'"
        [disabled]="saving || busy || uploading > 0"
        (onChangeClick)="save()"
      >
        {{ (saving ? 'saving' : isNew ? 'create' : 'save') | translate }}
      </app-button>
    </footer>
  }
</section>

<ng-template #rowInput let-row="row" let-field="field" let-placeholder="placeholder">
  <input
    type="text"
    [attr.maxlength]="placeholder ? null : 64"
    [attr.inputmode]="placeholder ? 'decimal' : null"
    class="h-8 w-full min-w-0 rounded-md border bg-white px-2 text-sm text-slate-800 outline-none focus:!border-orange-400"
    [ngClass]="[row.invalid.has(field) ? '!border-red-400' : '!border-slate-200', placeholder ? 'text-right' : '']"
    [placeholder]="placeholder ?? ''"
    [(ngModel)]="row[field]"
    (ngModelChange)="row.invalid.delete(field)"
  />
</ng-template>

<ng-template #quickStockPanel let-action="action">
  <div
    class="flex flex-wrap items-end gap-3 rounded-lg border px-3 py-2.5"
    [ngClass]="action.type === 'RECEIPT' ? '!border-emerald-200 bg-emerald-50/50' : '!border-rose-200 bg-rose-50/40'"
    (keydown.enter)="$any($event.target).tagName === 'INPUT' && submitQuickStock(action)"
    (keydown.escape)="$any($event.target).tagName === 'INPUT' && (quickStock = null)"
  >
    <div class="flex basis-full flex-wrap items-center gap-x-2 gap-y-1">
      <app-icon
        class="!h-5 !w-5 !text-[20px] !leading-none"
        [ngClass]="action.type === 'RECEIPT' ? 'text-emerald-700' : 'text-rose-600'"
        >{{ action.type === 'RECEIPT' ? 'move_to_inbox' : 'outbox' }}</app-icon
      >
      <span class="text-sm font-semibold text-slate-800">{{
        'stock.product_form.quick_stock.' + action.type | translate
      }}</span>
      <span class="text-xs" [ngClass]="action.invalid.size ? 'text-red-600' : 'text-slate-500'">
        @if (action.invalid.size) {
          {{ 'stock.product_form.quick_stock.invalid' | translate }}
        } @else if (action.balances[action.warehouse_id ?? '']; as balance) {
          {{
            'stock.product_form.quick_stock.balance'
              | translate
                : {
                    quantity: formatQuantity(balance.quantity),
                    available: formatQuantity(balance.available),
                    unit: unitShortName,
                  }
          }}
        } @else {
          {{ 'stock.product_form.quick_stock.no_balance' | translate }}
        }
      </span>
    </div>

    <div class="flex w-52 flex-col gap-1">
      <span class="text-[11px] font-medium text-slate-500">{{ 'warehouse' | translate }}</span>
      @if (warehouses.length) {
        <app-select
          class="block"
          [ngClass]="action.invalid.has('warehouse_id') ? '[&_.p-select]:!border-red-400' : ''"
          size="small"
          [options]="warehouses"
          optionLabel="name"
          optionValue="id"
          [filter]="warehouses.length > 7"
          [(ngModel)]="action.warehouse_id"
          (ngModelChange)="action.invalid.delete('warehouse_id')"
        />
      } @else {
        <button
          type="button"
          class="h-8 text-left text-sm font-semibold text-orange-600 hover:text-orange-700"
          (click)="createWarehouse()"
        >
          {{ 'stock.product_form.create_warehouse' | translate }}
        </button>
      }
    </div>
    <div class="flex w-28 flex-col gap-1">
      <span class="text-[11px] font-medium text-slate-500"
        >{{ 'stock.quantity' | translate }}{{ unitShortName ? ', ' + unitShortName : '' }}</span
      >
      <ng-container
        [ngTemplateOutlet]="rowInput"
        [ngTemplateOutletContext]="{ row: action, field: 'quantity', placeholder: '0' }"
      />
    </div>
    @if (action.type === 'RECEIPT') {
      <div class="flex w-32 flex-col gap-1">
        <span class="whitespace-nowrap text-[11px] font-medium text-slate-500"
          >{{ 'stock.document.purchase_price' | translate }}, {{ currencySymbol }}</span
        >
        <ng-container
          [ngTemplateOutlet]="rowInput"
          [ngTemplateOutletContext]="{ row: action, field: 'price', placeholder: '0' }"
        />
      </div>
    }
    <div class="flex min-w-40 flex-1 flex-col gap-1">
      <span class="text-[11px] font-medium text-slate-500">{{
        (action.type === 'WRITE_OFF' ? 'stock.document.write_off_reason' : 'comment') | translate
      }}</span>
      <input
        type="text"
        maxlength="1000"
        class="h-8 w-full min-w-0 rounded-md border !border-slate-200 bg-white px-2 text-sm text-slate-800 outline-none focus:!border-orange-400"
        [(ngModel)]="action.reason"
      />
    </div>

    <div class="flex gap-2">
      <app-button btnType="stroked" [disabled]="action.saving" (onChangeClick)="quickStock = null">
        {{ 'cancel' | translate }}
      </app-button>
      <app-button [disabled]="action.saving" (onChangeClick)="submitQuickStock(action)">
        {{ (action.saving ? 'saving' : 'stock.product_form.quick_stock.submit_' + action.type) | translate }}
      </app-button>
    </div>
  </div>
</ng-template>
